import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("overUnderBaseline.daily"), value: "day" }],
  week: [{ text: t("overUnderBaseline.weekly"), value: "week" }],
  month: [{ text: t("overUnderBaseline.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
    units: "metric",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units, type } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesVolume = _.get(snapshot, "data.exercisesVolume", {});
  const exercisesVolumeMean = _.get(snapshot, "data.exercisesVolumeMean", {});
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetrics = getSelectedMetrics(exercisesVolume, query.type);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);

  const baselineVolume = locals.fromMetric(
    _.get(exercisesVolumeMean, query.type),
    units
  );
  const selectedMetricsGroups = _.values(selectedMetrics);
  const totalVolumes = _.map(keys, (key) =>
    locals.fromMetric(_.sumBy(selectedMetricsGroups, `${key}.volume`), units)
  );
  const volumeSeries = [
    {
      name: query.type,
      className: query.type,
      data: _.map(totalVolumes, (val) => val - baselineVolume),
    },
  ];

  return (
    <div className="body metrics">
      <h1 className="alt">{t("overUnderBaseline.title")}</h1>
      <p>
        {t("overUnderBaseline.description")}
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="baselineTimeSpan"
                options={[
                  { text: t("overUnderBaseline.sixMonths"), value: "6-months" },
                  { text: t("overUnderBaseline.threeMonths"), value: "3-months" },
                  { text: t("overUnderBaseline.oneMonth"), value: "1-month" },
                ]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("overUnderBaseline.kg"), value: "metric" },
                  { text: t("overUnderBaseline.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("overUnderBaseline.total"), value: "total" },
                  { text: t("overUnderBaseline.squat"), value: "squat" },
                  { text: t("overUnderBaseline.bench"), value: "bench" },
                  { text: t("overUnderBaseline.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetrics) && (
            <div>
              <div className="legend">
                <div className={`bar series ${query.type}`}>
                  {t("overUnderBaseline.varianceFromBaseline")}
                </div>
              </div>
              <div className="axis-labels">
                <div className="left">{t("overUnderBaseline.volume")}</div>
                <div className="right">{t("overUnderBaseline.variance")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        referenceValue: 0,
                        labelInterpolationFnc: (val) => {
                          if (val === 0) return "-";
                          return val;
                        },
                      },
                    }}
                  />

                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: volumeSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        referenceValue: 0,
                        scaleMinSpace: 30,
                        labelInterpolationFnc: (val) => {
                          if (val === 0)
                            return "-&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;";
                          return `${Math.round((val / baselineVolume) * 100)}%`;
                        },
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: volumeSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 40}px`,
                      height: "380px",
                      stackBars: true,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, period);
                      const dayRange = getDayRangeString(
                        [date, date.clone().add(1, period).subtract(1, "day")],
                        query
                      );
                      const totalVolume = totalVolumes[i];
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              {t("overUnderBaseline.volume")}{" "}
                              <b>{locals.withWeight(totalVolume, units)}</b>{" "}
                            </div>
                            <div>
                              {t("overUnderBaseline.variance")}{" "}
                              <b>
                                {_.round(
                                  ((totalVolume - baselineVolume) /
                                    baselineVolume) *
                                    100
                                )}
                                %
                              </b>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
